import { KITCHEN_CALL, READY_BELL_STORAGE_KEY, readyBellEnabled, useChimeOutput } from '@agos/shared'
import { toast } from '@agos/ui'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useId, useRef } from 'react'

import {
  KITCHEN_CALL_TOAST_MS,
  KITCHEN_CALLS_POLL_MS,
  kitchenCallMessage,
  kitchenCallsOf,
  kitchenCallsParams,
  kitchenCallStep,
  type KitchenCall,
} from './kitchen-calls'
import { readKitchenCalls } from './kitchen-calls-api'

/**
 * The waiters' half of the *zvonček* - FRONTEND.md §9.720, §9.721, owner O95.
 *
 * <p>Mounted once on each screen a waiter works from - *Otvorené stoly* and its channel boards, *Mapovanie stolov*,
 * *Nová objednávka* - and polls `GET /kitchen/calls` every five seconds. `kitchenCallStep` decides what rings: the
 * first answer only sets the mark, a later call rings once and is shown, a failed read rings nothing.
 *
 * <p><b>Its own query key per mounted screen</b> (`useId`, and nothing kept once it unmounts), so a screen opened
 * after another never starts from that screen's cached answer and so never rings for a call made before it opened.
 *
 * <p><b>The ready bell's switch is read when it rings</b>, not when the screen opened: *Otvorené stoly* carries that
 * switch, and a hook that captured it on mount would keep ringing after a waiter pressed it off on the same screen.
 */
export function useKitchenCalls(): void {
  const screen = useId()
  const mark = useRef<number | null>(null)
  const output = useChimeOutput()

  const { data } = useQuery({
    queryKey: ['kitchen-calls', screen],
    queryFn: () => readKitchenCalls(kitchenCallsParams(mark.current)),
    refetchInterval: KITCHEN_CALLS_POLL_MS,
    gcTime: 0,
  })

  useEffect(() => {
    if (data === undefined) return
    const step = kitchenCallStep(mark.current, kitchenCallsOf(data))
    mark.current = step.mark
    if (!step.ring) return
    if (readyBellSwitchedOn()) output(KITCHEN_CALL)
    announceKitchenCalls(step.announce)
  }, [data, output])
}

/** The ready bell's switch, as stored on this device - on unless it was switched off, §9.675. */
function readyBellSwitchedOn(): boolean {
  try {
    return readyBellEnabled(localStorage.getItem(READY_BELL_STORAGE_KEY))
  } catch {
    // A browser refusing storage keeps the bell's default rather than going quiet.
    return true
  }
}

/**
 * One toast for one poll, a line per call.
 *
 * <p><b>One toast rather than one per call</b>: the toaster shows one at a time (`TOAST_LIMIT = 1`, ported from
 * legacy), so a toast per call would leave only the last on screen and *several calls show each* would be false.
 */
function announceKitchenCalls(calls: readonly KitchenCall[]): void {
  if (calls.length === 0) return
  toast({
    title: (
      <span data-kitchen-call-toast className="flex flex-col gap-0.5">
        {calls.map((call) => (
          <span key={call.id} data-kitchen-call-line={call.id}>
            {kitchenCallMessage(call)}
          </span>
        ))}
      </span>
    ),
    duration: KITCHEN_CALL_TOAST_MS,
  })
}
